import { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { CommandCenterShell } from '../../shell'
import { RESOURCES } from '../../../Projects/accessControl'
import {
  PREVIEW_PERSONAS, PREVIEW_STATE_META, defaultGate, previewPersona,
  projectAllModules, projectAllResources,
  type PreviewPersonaKey, type GateState, type ProjectedRow,
} from './previewPolicy'
import './owner-preview.css'

/**
 * OwnerPreviewConsole — owner-only, non-mutating "preview as a persona"
 * console over the real Portal Factory module catalog and the real
 * Commandery access-permission matrix.
 *
 * Honesty / safety contract (do not weaken):
 *   - This NEVER creates an external session, mints a token, or impersonates
 *     a person. Everything renders under the OWNER's own authenticated
 *     session (the route wrapper already required isAdmin before mounting
 *     this component).
 *   - Every projected row comes from previewPolicy.ts's pure functions —
 *     no real message/file/meeting content is ever fetched or rendered here.
 *   - The only real (owner-scoped, read-only) data are the project list and
 *     that project's real sq_project_members rows for optional context —
 *     no write call exists anywhere in this component.
 *   - Invitation/agreement/expiration/revocation are OWNER-CHOSEN SIMULATED
 *     inputs — no real agreement/signature system exists in the repo yet
 *     (see the NDA readiness audit in this PR).
 *
 * CHAT #8 conversion: presentation moved to owner-preview.css, consuming the
 * shared .sqv-surface foundation (sq-v2-surface.css). The only inline style
 * left is the genuinely dynamic per-viewport max-width (--sqv-op-max-width).
 */

export interface ProjectSummary {
  id: string
  name: string
  client_name: string | null
}

export interface MemberSummary {
  id: string
  role: string
  status: string
}

export interface OwnerPreviewConsoleProps {
  isAdmin: boolean

  projects: ProjectSummary[]
  projectsLoading: boolean
  projectsError: string | null
  projectsAuthRequired: boolean
  selectedProjectId: string | null
  onSelectProject: (id: string | null) => void

  membersActivated: boolean
  members: MemberSummary[]
  membersLoading: boolean
  membersError: string | null

  onHome?: () => void
  onBackCommandCenter?: () => void
}

const VIEWPORTS = [
  { key: 'desktop_1440', label: 'Desktop · 1440', width: 1440 },
  { key: 'desktop_1024', label: 'Desktop · 1024', width: 1024 },
  { key: 'tablet_768', label: 'Tablet · 768', width: 768 },
  { key: 'mobile_390', label: 'Mobile · 390', width: 390 },
] as const
type ViewportKey = (typeof VIEWPORTS)[number]['key']

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return <section className="sqv-op-section"><h2 className="sqv-op-heading">{title}</h2>{children}</section>
}

function StateBadge({ row }: { row: ProjectedRow }) {
  return (
    <div className="sqv-op-badge">
      <span className="sqv-op-badge-chip" data-preview-state={row.state}>
        {PREVIEW_STATE_META[row.state].label.toUpperCase()}
      </span>
      <div className="sqv-op-badge-body">
        <span className="sqv-op-badge-label">{row.label.replace(/_/g, ' ')}</span>
        <span className="sqv-op-badge-reason">{row.reason}</span>
      </div>
    </div>
  )
}

function GateToggle({ checked, onChange, text }: { checked: boolean; onChange: (v: boolean) => void; text: string }) {
  return (
    <label className="sqv-op-gate-toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} /> {text}
    </label>
  )
}

export default function OwnerPreviewConsole(props: OwnerPreviewConsoleProps) {
  const {
    isAdmin, projects, projectsLoading, projectsError, projectsAuthRequired,
    selectedProjectId, onSelectProject,
    membersActivated, members, membersLoading, membersError,
    onHome, onBackCommandCenter,
  } = props

  const [personaKey, setPersonaKey] = useState<PreviewPersonaKey>('client')
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null)
  const [viewport, setViewport] = useState<ViewportKey>('desktop_1440')
  const [gate, setGate] = useState<GateState>(defaultGate())

  const persona = previewPersona(personaKey)
  const viewportMeta = VIEWPORTS.find((v) => v.key === viewport)!
  const moduleRows = useMemo(() => projectAllModules(personaKey, gate), [personaKey, gate])
  const resourceRows = useMemo(() => projectAllResources(personaKey, gate), [personaKey, gate])
  const selectedMember = members.find((m) => m.id === selectedMemberId) ?? null

  if (!isAdmin) {
    return (
      <div className="sqv-op-denied">
        <div className="sqv-op-denied__inner">
          <div className="sqv-op-denied__title">Owner / admin only</div>
          <p className="sqv-op-denied__body">The Owner Preview console is restricted to owner/admin operators.</p>
        </div>
      </div>
    )
  }

  const contextBar = (
    <div role="status" aria-live="assertive" className="sqv-op-banner">
      PREVIEW MODE — NO ACTIONS WILL BE SENT AS THIS USER
    </div>
  )

  const rightRail = (
    <div className="sqv-surface sqv-op-rail" data-sqv-surface="owner-preview">
      <Section title="Persona">
        <select
          value={personaKey}
          onChange={(e) => { setPersonaKey(e.target.value as PreviewPersonaKey); setSelectedMemberId(null) }}
          aria-label="Preview persona"
          className="sqv-op-select sqv-op-select--persona"
        >
          {PREVIEW_PERSONAS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        <p className="sqv-op-persona-note">
          {persona.derived ? 'DERIVED — ' : 'DIRECT MATCH — '}{persona.canonicalNote}
        </p>
      </Section>

      <Section title="Portal / access state (simulated)">
        <div className="sqv-op-gate">
          <GateToggle checked={gate.started} onChange={(v) => setGate((g) => ({ ...g, started: v }))} text="Access has started" />
          <GateToggle checked={gate.expired} onChange={(v) => setGate((g) => ({ ...g, expired: v }))} text="Access has expired" />
          <GateToggle checked={gate.suspended} onChange={(v) => setGate((g) => ({ ...g, suspended: v }))} text="Access is suspended" />
          <GateToggle checked={gate.revoked} onChange={(v) => setGate((g) => ({ ...g, revoked: v }))} text="Access is revoked" />
          <GateToggle checked={gate.ndaRequired} onChange={(v) => setGate((g) => ({ ...g, ndaRequired: v }))} text="NDA required" />
          {gate.ndaRequired && (
            <GateToggle checked={gate.ndaComplete} onChange={(v) => setGate((g) => ({ ...g, ndaComplete: v }))} text="NDA complete" />
          )}
          <p className="sqv-op-gate-note">
            Simulated toggles — no real invitation or agreement record exists yet (see NDA readiness audit).
          </p>
        </div>
      </Section>

      <Section title="Viewport">
        <select value={viewport} onChange={(e) => setViewport(e.target.value as ViewportKey)} aria-label="Preview viewport" className="sqv-op-select">
          {VIEWPORTS.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
        </select>
      </Section>
    </div>
  )

  let membersBody: React.ReactNode
  if (!selectedProjectId) {
    membersBody = <p className="sqv-op-note sqv-op-note--sm">Select a project to see its real members (optional context — the projection above never depends on a specific person).</p>
  } else if (membersLoading) {
    membersBody = <p className="sqv-op-note sqv-op-note--sm">Loading…</p>
  } else if (membersError) {
    membersBody = <p className="sqv-op-note sqv-op-note--sm sqv-op-note--error">{membersError}</p>
  } else if (!membersActivated) {
    membersBody = <p className="sqv-op-note sqv-op-note--sm">Project membership is not activated for this project yet.</p>
  } else if (members.length === 0) {
    membersBody = <p className="sqv-op-note sqv-op-note--sm">No real project members yet for this project.</p>
  } else {
    membersBody = (
      <div className="sqv-op-members">
        {members.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setSelectedMemberId(m.id === selectedMemberId ? null : m.id)}
            className="sqv-op-member-btn"
            data-selected={m.id === selectedMemberId}
          >
            {m.role} · {m.status}
          </button>
        ))}
      </div>
    )
  }

  return (
    <CommandCenterShell
      onHome={onHome}
      onBackCommandCenter={onBackCommandCenter}
      breadcrumb={['COMMAND CENTER', 'OLIVIUM', 'OWNER PREVIEW']}
      title="Owner Preview"
      honestyBadge={{
        label: 'PREVIEW · NOT A REAL SESSION',
        title: 'This console never creates an external session, mints a token, or impersonates a person. Every row is a policy projection computed from the real Commandery permission matrix and the real Portal Factory module catalog — no real message/file/meeting content is fetched or rendered here.',
      }}
      contextBar={contextBar}
      rightRail={rightRail}
    >
      <div className="sqv-surface sqv-op" data-sqv-surface="owner-preview">
        <Section title="Project">
          {projectsAuthRequired ? (
            <p className="sqv-op-note">Sign in to the live cockpit to select a project.</p>
          ) : projectsLoading ? (
            <p className="sqv-op-note">Loading…</p>
          ) : projectsError ? (
            <p className="sqv-op-note sqv-op-note--error">{projectsError}</p>
          ) : projects.length === 0 ? (
            <p className="sqv-op-note">No projects yet.</p>
          ) : (
            <select value={selectedProjectId ?? ''} onChange={(e) => onSelectProject(e.target.value || null)} aria-label="Select a project to preview" className="sqv-op-select">
              <option value="">— Select a project —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}{p.client_name ? ` · ${p.client_name}` : ''}</option>)}
            </select>
          )}
        </Section>

        <Section title="Specific person (optional context)">
          {membersBody}
          {selectedMember && (
            <p className="sqv-op-member-context">
              Context only — the projection below is driven by the selected persona above, not by this person's real data.
            </p>
          )}
        </Section>

        <Section title={`Modules — as ${persona.label} · ${viewportMeta.label}`}>
          <div className="sqv-op-modules-grid" style={{ ['--sqv-op-max-width' as string]: viewportMeta.width < 1024 ? `${viewportMeta.width}px` : 'none' } as CSSProperties}>
            {moduleRows.map((row) => <StateBadge key={row.key} row={row} />)}
          </div>
        </Section>

        <Section title={`Resources — as ${persona.label}`}>
          <div className="sqv-op-resources-grid">
            {resourceRows.map((row) => <StateBadge key={row.key} row={row} />)}
          </div>
          <p className="sqv-op-resource-note">
            {RESOURCES.length} resources from the existing Commandery Access Control matrix — reference data, not real content.
          </p>
        </Section>
      </div>
    </CommandCenterShell>
  )
}
